import { Typography, makeStyles } from "@material-ui/core"
import ScoringChartTooltip from "../atoms/tooltip/ScoringChartTooltip"
import { formatDisplayScore } from "../../utils/formatters"

interface QuestionnaireScoringProps {
  score: number
  scoreType: string
  severityColor: string | undefined
  severityLabel: string
  date: string
}

/**
 * QuestionnaireScore is a component that renders a small score tile for a
 * questionnaire result and wraps it in a hover tooltip.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {number} props.score - The score value to display.
 * @param {string} props.scoreType - The score type label shown in the tooltip.
 * @param {string} props.severityColor - The severity color used as the tile background (alpha applied).
 * @param {string} props.severityLabel - The severity label shown in the tooltip.
 * @param {string} props.date - ISO date/time string representing when the questionnaire was completed.
 */
export default function QuestionnaireScore({
  score,
  scoreType,
  date,
  severityColor,
  severityLabel,
}: QuestionnaireScoringProps) {
  const classes = useStyles()

  const color = severityColor ? `${severityColor}80` : "grey"
  const formattedScore = formatDisplayScore(score, scoreType)

  return (
    <div>
      <ScoringChartTooltip
        score={score}
        scoreType={scoreType}
        completedAt={date}
        label={severityLabel}
      >
        <div
          className={classes.questionnaireScore}
          style={{ backgroundColor: color }}
        >
          <Typography variant="body2">{formattedScore}</Typography>
        </div>
      </ScoringChartTooltip>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  questionnaireScore: {
    height: 33,
    width: 50,
    borderRadius: 4,
    padding: theme.spacing(1),
  },
}))
